import { FORMAT_DATEOFF, FORMAT_DD_MM_YYYY } from '../../constants';
import {
  ClosingDateContainer,
  ClosingHeader,
  ClosingRow,
  DateText,
  NoDateText,
  OperationTitle,
  OperationTitleRow,
  StyledDatepicker,
} from '../../createAndEditLocation.styled';
import { RemoveIcon } from '@/assets/svg';
import CustomAdapter from '@/new-components/CustomDate/CustomDateAdapter';
import CustomSelectContainer from '@/new-components/CustomSelectOld/CustomSelectContainer';
import { Box, Grid, makeStyles } from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import { LocalizationProvider, PickersDay } from '@material-ui/pickers';
import classnames from 'classnames';
import moment from 'moment';
import React, { useState, useRef, useMemo, useEffect } from 'react';

const useStyles = makeStyles((theme) => ({
  highlight: {
    borderRadius: '50%',
    backgroundColor: '#ad5e99',
    color: theme.palette.common.white,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  container: {
    display: 'flex',
    width: 850,
    margin: '0 auto',
    gap: '20px',
  },

  rightContainer: {
    padding: '20px 0',
    height: 'calc(100vh - 282px)',
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
  },

  listContainer: {
    flex: '1',
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 400px)',
  },

  paginationContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '16px 0',
  },

  leftContainer: {
    padding: '20px 0',
    flex: '1',

    height: 'calc(100vh - 282px)',
    maxWidth: 352,
    overflow: 'auto',
  },
}));

const OperationDate = ({ selectedDates, onSelectDate, disabled = false }) => {
  const classes = useStyles();
  const _tempDate = useRef(moment());
  const renderPickerDay = (date, _selectedDates, DayComponentProps) => {
    const formattedValue = date.format(FORMAT_DATEOFF);
    const isSelected = selectedDates.includes(formattedValue);

    return (
      <div
        onClick={() => (disabled ? {} : onSelectDate(date))}
        key={formattedValue}
      >
        <PickersDay
          {...DayComponentProps}
          disabled={disabled}
          selected={false}
          className={classnames(
            isSelected && {
              [classes.highlight]: true,
            }
          )}
        />
      </div>
    );
  };
  const [year, setYear] = useState([moment().year()]);
  const [date, setDate] = useState(moment());
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  const currentYear = new Date().getFullYear();
  const YEARS = Array.from({ length: 5 }, (_, index) => ({
    key: currentYear + index,
    value: currentYear + index,
  }))
    .reverse()
    .concat(
      Array.from({ length: 12 }, (_, index) => ({
        key: currentYear - index - 1,
        value: currentYear - index - 1,
      }))
    );

  const handleDatePickerChange = (value) => {
    _tempDate.current = value;
    onSelectDate && value && onSelectDate(value);
  };

  // Sort dates from latest to oldest and convert to DD/MM/YYYY format for display
  const sortedDates = useMemo(() => {
    if (!selectedDates.length) return [];
    return [...selectedDates]
      .sort((a, b) => {
        const dateA = moment(a, FORMAT_DATEOFF);
        const dateB = moment(b, FORMAT_DATEOFF);
        return dateB.valueOf() - dateA.valueOf();
      })
      .map((dateStr) => ({
        display: moment(dateStr, FORMAT_DATEOFF).format(FORMAT_DD_MM_YYYY),
        original: dateStr,
      }));
  }, [selectedDates]);

  // Paginate sorted dates
  const paginatedDates = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedDates.slice(startIndex, endIndex);
  }, [sortedDates, page, itemsPerPage]);

  const totalPages = Math.ceil(sortedDates.length / itemsPerPage);

  // Reset to page 1 if current page is invalid after dates change
  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(1);
    }
  }, [totalPages, page]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.leftContainer}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <OperationTitleRow>
              <OperationTitle>Closing date</OperationTitle>
              <CustomSelectContainer
                isFullWidth
                filterValue={year}
                data={YEARS}
                renderValue={<>{year.length ? year[0] : 'Select a year'}</>}
                isMutiple={false}
                onChange={(e) => {
                  setYear([e.target.value]);

                  setDate(moment(_tempDate.current).year(e.target.value));
                }}
              />
            </OperationTitleRow>
          </Grid>
          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={CustomAdapter}>
              <StyledDatepicker
                value={date}
                displayStaticWrapperAs="desktop"
                onChange={handleDatePickerChange}
                renderDay={renderPickerDay}
                // renderInput={(props) => <TextField {...props} />}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      </Box>
      <Box className={classes.rightContainer}>
        <Grid item xs={12}>
          <ClosingDateContainer>
            <ClosingHeader container>
              <Grid item xs={10}>
                Closing dates
              </Grid>
              <Grid item xs={2}>
                Action
              </Grid>
            </ClosingHeader>
            <Box className={classes.listContainer}>
              {sortedDates.length ? (
                paginatedDates.map((dateObj) => (
                  <ClosingRow container key={dateObj.original}>
                    <Grid item xs={10}>
                      <DateText>{dateObj.display}</DateText>
                    </Grid>

                    <Grid item xs={2}>
                      <Box
                        style={{ cursor: 'pointer' }}
                        onClick={() =>
                          onSelectDate(moment(dateObj.original, FORMAT_DATEOFF))
                        }
                      >
                        <RemoveIcon />
                      </Box>
                    </Grid>
                  </ClosingRow>
                ))
              ) : (
                <ClosingRow container>
                  <Grid item xs={8}>
                    <NoDateText>No date selected</NoDateText>
                  </Grid>
                  <Grid item xs={2}></Grid>
                  <Grid item xs={2}></Grid>
                </ClosingRow>
              )}
            </Box>
          </ClosingDateContainer>
          {sortedDates.length > itemsPerPage && (
            <Box className={classes.paginationContainer}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default OperationDate;
